import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { matchPath, useLocation, useSearchParams, useNavigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { ClickstreamScene } from '@zenbusiness/application-commons-clickstream';
import { useSlideCheckout } from '@zenbusiness/application-commons-ecommerce';
import { FederatedModule } from '@zenbusiness/application-commons-federation';
import { usePriceList } from '@zenbusiness/application-commons-pricelist';

import { FEDERATED } from '#client/build-env';
import * as ROUTES from '#client/constants/routes';
import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { useUrlResolver } from '#client/hooks/useUrlResolver';
import { useProductCoupon } from '#client/pages/ProductLanding/hooks/useProductCoupon';

const FLOW_ID = 'dashboard-checkout';
const FLOW_VERSION = 1;

// Shared by both amendment services. `success` replaces the drawer's post-payment view.
const amendmentCheckout = ({ isComplianceAmendmentsPage }) => {
  // Compliance amendments page adds additional slot overrides that we do not
  // want to show on the product page.
  if (!isComplianceAmendmentsPage) {
    return null;
  }

  return {
    slots: {
      upsell: (
        <FederatedModule {...FEDERATED.ComplianceUi} exportName="AmendmentsCheckoutCallout" loadingElement={<span />} />
      ),
      success: (
        <FederatedModule
          {...FEDERATED.ComplianceUi}
          exportName="AmendmentsCheckoutSuccessCallout"
          loadingElement={<span />}
        />
      ),
    },
  };
};

// What each checkout adds to the drawer, keyed by the service that names it. Entries are
// built per render rather than held as elements so their slots can be handed the callbacks
// below: an add-on decides for itself whether it renders, and has to be able to say so.
const SERVICE_CHECKOUTS = {
  business_license_filing: () => ({
    slots: {
      subscription: <FederatedModule {...FEDERATED.ComplianceUi} exportName="BusinessLicenseFilingCheckoutCallout" />,
    },
  }),
  business_license_report: ({ onUpsellVisibilityChange }) => ({
    slots: {
      upsell: (
        <FederatedModule
          {...FEDERATED.ComplianceUi}
          exportName="EinUpsellCheckoutCallout"
          loadingElement={<span />}
          onVisibilityChange={onUpsellVisibilityChange}
        />
      ),
    },
    // The add-on above shows the EIN's own name and price, so the cart must not list it as a
    // row too — the customer would see the same charge twice. Applied only while the add-on
    // reports itself on screen: it opts out behind its feature flag, for customers who already
    // own an EIN, for plans that bundle one, for price lists without an EIN, and by failing to
    // load at all. Hiding the row regardless would bill those customers for a line item that
    // appears nowhere. Suppresses the row only; the order total still includes the service.
    upsellCartServiceNames: ['ein_creation'],
  }),
  corp_amendment: amendmentCheckout,
  llc_amendment: amendmentCheckout,
};

// SQSP SKUs are matched by servicePricingUuid instead: callers pass only the pricing
// uuid (the cart's source of truth) and we resolve it against the price list here.
//
// loadingElement is required here — without it, the FederatedModule falls back to
// the app's full-screen loading logo while this one line of text loads.
const SQSP_DOMAIN_SLOTS = {
  subscription: <FederatedModule {...FEDERATED.WebsitesUi} exportName="DomainCheckoutTOS" loadingElement={<span />} />,
};

const UUID_MATCHED_SERVICE_SLOTS = {
  domain_name_reg_sqsp_tier1: SQSP_DOMAIN_SLOTS,
  domain_name_reg_sqsp_tier2: SQSP_DOMAIN_SLOTS,
  domain_name_reg_sqsp_tier3: SQSP_DOMAIN_SLOTS,
  domain_name_reg_sqsp_tier4: SQSP_DOMAIN_SLOTS,
  website_sqsp_core: {
    subscription: (
      <FederatedModule {...FEDERATED.WebsitesUi} exportName="WebsiteCheckoutTOS" loadingElement={<span />} />
    ),
  },
};

const UUID_MATCHED_SERVICE_NAMES = Object.keys(UUID_MATCHED_SERVICE_SLOTS);

// The page the open checkout was opened on. Kept outside the component because moving between route trees
// remounts it while the checkout stays open.
let openedOnPathname = null;

const SlideCheckout = () => {
  const navigate = useNavigate();
  const { open, openCheckout, closeCheckout, onClose, servicePricingUuids, serviceNames, servicesToAddToCart } =
    useSlideCheckout();
  const { get, priceListUuid, priceListName } = usePriceList();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const redirectPathRef = useRef(null);
  const dashboardPath = useDashboardPath();
  const resolve = useUrlResolver();
  const { entitlements } = useBusinessEntity();
  // Whether this checkout's add-on is on screen. It reports in as it mounts and unmounts;
  // until it does, nothing is hidden, so a drawer whose add-on never renders still lists every
  // service the order total charges for.
  const [isUpsellVisible, setIsUpsellVisible] = useState(false);
  const handleUpsellVisibilityChange = useCallback((isVisible) => setIsUpsellVisible(Boolean(isVisible)), []);

  // Parse URL parameters for checkout
  const checkoutName = searchParams.get('checkout');
  const filter = searchParams.get('filter');
  const parsedFilter = useMemo(() => {
    try {
      // A malformed ?filter= must not throw during render. JSON.parse also succeeds on
      // scalars — `null` above all, which would throw on the `.interval` reads below — so
      // the parsed shape is checked, not just the parse.
      const parsed = filter ? JSON.parse(filter) : null;
      return parsed && typeof parsed === 'object' ? parsed : { interval: 'annual' };
    } catch {
      return { interval: 'annual' };
    }
  }, [filter]);

  // Check for applicable coupon for URL parameter checkout
  const { serviceToAddToCart: urlParamServiceToAdd } = useProductCoupon({
    entitlements,
    serviceCode: checkoutName || '',
    interval: parsedFilter.interval || 'annual',
  });

  const onCloseCheckout = useCallback(
    ({ isPurchaseComplete }) => {
      closeCheckout();
      if (isPurchaseComplete && redirectPathRef.current) {
        // Hard-navigate (full reload) when the resolved entry sets forceReload; otherwise SPA-navigate.
        if (redirectPathRef.current.forceReload) {
          window.location.href = redirectPathRef.current.path;
        } else {
          navigate(redirectPathRef.current.path);
        }
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  // Listen for the parameter "checkout" in the URL to open the dashboard checkout modal
  // filter must be in the following format: &filter={"interval":"annual","price":200}
  // no filter will default to annual pricing if there is more than one service pricing option
  useEffect(() => {
    if (priceListUuid && checkoutName) {
      const requestedServicePricing = get([checkoutName], parsedFilter);

      const requestedServicePricingUuids = requestedServicePricing
        ?.map(({ pricing }) => pricing?.servicePricingUuid)
        .flat();

      if (requestedServicePricingUuids?.length) {
        // Set the redirect path in the ref before opening checkout. The canonical
        // resolver owns the service→destination mapping (checkout_slide context);
        // an unmapped service falls back to the dashboard root.
        const { url, forceReload, found } = resolve(checkoutName, { context: 'checkout_slide' });
        redirectPathRef.current = found ? { path: url, forceReload } : { path: dashboardPath, forceReload: false };

        // Use servicesToAddToCart format when coupon is available (includes coupon UUID)
        if (urlParamServiceToAdd) {
          openCheckout({
            servicesToAddToCart: [urlParamServiceToAdd],
            onClose: onCloseCheckout,
          });
        } else {
          // Fall back to legacy format when no coupon is applicable
          openCheckout({
            serviceNames: [checkoutName],
            servicePricingUuids: requestedServicePricingUuids,
            interval: parsedFilter.interval || 'annual',
            onClose: onCloseCheckout,
          });
        }

        // Remove the checkout parameter from the URL
        setSearchParams((params) => {
          params.delete('checkout');
          params.delete('filter');
          return params;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, priceListUuid, urlParamServiceToAdd]);

  // Close when the user leaves the page the checkout opened on. Uses closeCheckout, not onClose, because openers
  // may navigate in onClose.
  useEffect(() => {
    if (!open) {
      openedOnPathname = null;
    } else if (openedOnPathname === null) {
      openedOnPathname = pathname;
    } else if (openedOnPathname !== pathname) {
      closeCheckout();
    }
  }, [open, pathname, closeCheckout]);

  // pricing uuid -> slots, derived ONLY from the services declared in
  // UUID_MATCHED_SERVICE_SLOTS; any other product's uuid misses this map, so no
  // slot can load for unlisted services.
  const uuidMatchedSlotsByPricingUuid = useMemo(() => {
    if (!priceListUuid) {
      return {};
    }

    const products = get(UUID_MATCHED_SERVICE_NAMES) ?? [];
    return Object.fromEntries(
      products
        .map((product) => [product.pricing?.servicePricingUuid, UUID_MATCHED_SERVICE_SLOTS[product.service]])
        .filter(([servicePricingUuid]) => servicePricingUuid),
    );
  }, [get, priceListUuid]);

  // The first declared service names the checkout; callers pass either shape.
  const serviceName = serviceNames[0] ?? servicesToAddToCart[0]?.serviceName;

  const isComplianceAmendmentsPage = Boolean(matchPath(ROUTES.entityComplianceAmendments, pathname));

  // hasOwn, not a bare lookup: entries are called, and `SERVICE_CHECKOUTS['constructor']`
  // would otherwise hand back a prototype method to invoke.
  const serviceCheckout = useMemo(
    () =>
      Object.hasOwn(SERVICE_CHECKOUTS, serviceName)
        ? SERVICE_CHECKOUTS[serviceName]({
            onUpsellVisibilityChange: handleUpsellVisibilityChange,
            isComplianceAmendmentsPage,
          })
        : null,
    [serviceName, isComplianceAmendmentsPage, handleUpsellVisibilityChange],
  );

  const slots = useMemo(() => {
    const servicePricingUuid = servicesToAddToCart[0]?.servicePricingUuid ?? servicePricingUuids[0];

    return serviceCheckout?.slots ?? uuidMatchedSlotsByPricingUuid[servicePricingUuid] ?? null;
  }, [servicesToAddToCart, servicePricingUuids, serviceCheckout, uuidMatchedSlotsByPricingUuid]);

  // Only the name-matched checkouts sell an add-on, so this tracks SERVICE_CHECKOUTS rather
  // than the uuid-matched map. Must stay undefined (never null) when nothing is hidden:
  // ecommerce-ui's useSortCartItems supplies its default by destructuring, which null defeats
  // — it would reach `hiddenServiceNames.join()`.
  const hiddenCartServiceNames = isUpsellVisible ? serviceCheckout?.upsellCartServiceNames : undefined;

  return priceListUuid ? (
    <ClickstreamScene
      flowId={FLOW_ID}
      flowVersion={FLOW_VERSION}
      flowPriceListUuid={priceListUuid}
      flowPriceListName={priceListName}
    >
      <FederatedModule
        {...FEDERATED.EcommerceUi}
        exportName="SlideCheckout"
        hiddenCartServiceNames={hiddenCartServiceNames}
        isOpen={open}
        onClose={onClose}
        priceListUuid={priceListUuid}
        servicePricingUuids={servicePricingUuids}
        slots={slots}
      />
    </ClickstreamScene>
  ) : null;
};

export default SlideCheckout;
